
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useUpdateSpecialty, type Specialty } from "@/hooks/useSpecialties";

interface EditSpecialtyDialogProps {
  specialty: Specialty | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EditSpecialtyDialog = ({ specialty, open, onOpenChange }: EditSpecialtyDialogProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);

  const updateSpecialty = useUpdateSpecialty();

  useEffect(() => {
    if (specialty) {
      setName(specialty.name);
      setDescription(specialty.description || "");
      setDisplayOrder(specialty.display_order);
      setIsActive(specialty.is_active);
    }
  }, [specialty]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!specialty) return;

    updateSpecialty.mutate({
      id: specialty.id,
      name,
      description: description || null,
      display_order: displayOrder,
      is_active: isActive,
    }, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  if (!specialty) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Specialty</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Ice Bath"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the specialty"
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="displayOrder">Display Order</Label>
            <Input
              id="displayOrder"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value))}
              placeholder="0"
              min="0"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <Label htmlFor="isActive">Active</Label>
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={updateSpecialty.isPending}
            >
              {updateSpecialty.isPending ? "Updating..." : "Update"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditSpecialtyDialog;
